import { useState, useEffect, useCallback, useRef } from 'react'
import { API_ENDPOINTS } from '../config/api'
import { getWebRTCConfig } from '../config/signaling'

/**
 * Yjs P2P Memory Hook - Integrates with existing useEtherith
 * Real-time P2P collaboration first, then D1 archival
 */
export function useYjsP2PMemories(wallet, user, isAuthenticated) {
  const [isP2PInitialized, setIsP2PInitialized] = useState(false)
  const [activeCollaborations, setActiveCollaborations] = useState([])
  const [collaborativeMemories, setCollaborativeMemories] = useState([])
  const [p2pStatus, setP2PStatus] = useState('initializing')
  const [activeUsers, setActiveUsers] = useState([])

  // Refs for Yjs P2P components
  const collaborationInstanceRef = useRef(null)
  const activeDocumentsRef = useRef(new Map())
  const providersRef = useRef(new Map())
  const awarenessRef = useRef(new Map())

  /**
   * Initialize Yjs P2P system
   */
  const initializeP2P = useCallback(async () => {
    if (!isAuthenticated || !wallet || isP2PInitialized) return

    try {
      console.log('🚀 Initializing Yjs P2P Memory System...')
      setP2PStatus('connecting')

      // Use centralized Yjs loader to prevent duplicate imports
      const yjsInstance = await import('../utils/yjsLoader.js').then(module => module.getYjsInstance())
      const { Y, WebsocketProvider, WebrtcProvider, IndexeddbPersistence } = yjsInstance

      // Create collaboration instance (similar to your frontend integration)
      const EtherithMemoryCollaboration = createCollaborationClass(Y, WebsocketProvider, WebrtcProvider, IndexeddbPersistence)
      
      const collaboration = new EtherithMemoryCollaboration({
        apiBase: API_ENDPOINTS.YJS_P2P_PUBLISHER, // Your deployed worker
        userId: user.id,
        userName: user.username || user.display_name,
        deviceId: `web-${Date.now()}`,
        onActiveUsersChanged: handleActiveUsersChanged,
        onContentChanged: handleContentChanged,
        onSyncStateChanged: handleSyncStateChanged,
        // Pass Yjs instances from centralized loader
        Y: Y,
        WebsocketProvider: WebsocketProvider,
        WebrtcProvider: WebrtcProvider,
        IndexeddbPersistence: IndexeddbPersistence
      })

      collaborationInstanceRef.current = collaboration
      setIsP2PInitialized(true)
      setP2PStatus('connected')

      console.log('✅ Yjs P2P Memory System initialized')

      // Load existing collaborative sessions
      await loadActiveCollaborations()

    } catch (error) {
      console.error('❌ Failed to initialize P2P system:', error)
      setP2PStatus('error')
    }
  }, [isAuthenticated, wallet, user, isP2PInitialized])

  /**
   * Publish a memory using P2P first, then D1 archival
   */
  const publishCollaborativeMemory = useCallback(async (memoryData) => {
    if (!collaborationInstanceRef.current) {
      throw new Error('P2P system not initialized')
    }

    try {
      console.log('📤 Publishing collaborative memory:', memoryData.title)
      setP2PStatus('publishing')

      // Use the collaborative publishing system
      const result = await collaborationInstanceRef.current.publishCollaborativeMemory({
        title: memoryData.note || memoryData.title,
        content: memoryData.content || memoryData.note,
        contentType: memoryData.type || 'text/plain',
        tags: memoryData.tags || [],
        visibility: memoryData.preservationMode === 'public' ? 'public' : 'unlisted',
        isRealtime: true // Enable real-time collaboration
      })

      if (result.success) {
        console.log('✅ Memory published to P2P network:', result.memoryId)
        
        // Add to local state
        const collaborativeMemory = {
          ...memoryData,
          id: result.memoryId,
          collaborationUrl: result.documentUrl,
          collaboration: result.collaboration,
          isCollaborative: true,
          status: 'collaborative',
          p2pStatus: 'active'
        }

        setCollaborativeMemories(prev => [collaborativeMemory, ...prev])
        setP2PStatus('published')

        return {
          success: true,
          memoryId: result.memoryId,
          collaboration: result.collaboration,
          isCollaborative: true
        }
      } else {
        throw new Error(result.error)
      }
    } catch (error) {
      console.error('❌ Failed to publish collaborative memory:', error)
      setP2PStatus('error')
      throw error
    }
  }, [])

  /**
   * Join an existing collaborative memory
   */
  const joinCollaborativeMemory = useCallback(async (memoryId) => {
    if (!collaborationInstanceRef.current) {
      throw new Error('P2P system not initialized')
    }

    try {
      console.log(`🔗 Joining collaborative memory: ${memoryId}`)
      
      const collaboration = await collaborationInstanceRef.current.joinCollaborativeMemory(memoryId)
      
      // Store the collaboration interface
      activeDocumentsRef.current.set(memoryId, collaboration)
      
      console.log(`✅ Joined collaborative memory: ${memoryId}`)
      return collaboration
    } catch (error) {
      console.error(`❌ Failed to join collaborative memory ${memoryId}:`, error)
      throw error
    }
  }, [])

  /**
   * Get archived memories from D1 (non-collaborative viewing)
   */
  const getArchivedMemories = useCallback(async (filters = {}) => {
    if (!collaborationInstanceRef.current) {
      return { memories: [], total: 0 }
    }

    try {
      const result = await collaborationInstanceRef.current.getArchivedMemories({
        userId: filters.userId || user.id,
        tags: filters.tags,
        visibility: filters.visibility || 'public',
        limit: filters.limit || 50,
        offset: filters.offset || 0
      })

      console.log(`📖 Loaded ${result.memories.length} archived memories from D1`)
      return result
    } catch (error) {
      console.error('❌ Failed to load archived memories:', error)
      return { memories: [], total: 0 }
    }
  }, [user])

  /**
   * Get currently active collaborative sessions
   */
  const loadActiveCollaborations = useCallback(async () => {
    if (!collaborationInstanceRef.current) return

    try {
      const sessions = await collaborationInstanceRef.current.getActiveCollaborativeSessions()
      setActiveCollaborations(sessions)
      console.log(`📊 Found ${sessions.length} active collaborative sessions`)
    } catch (error) {
      console.error('❌ Failed to load active collaborations:', error)
    }
  }, [])

  /**
   * Handle active users changes in collaborations
   */
  const handleActiveUsersChanged = useCallback(({ memoryId, activeUsers: users, changes }) => {
    console.log(`👥 Active users changed for ${memoryId}:`, users.length)
    setActiveUsers(prev => ({
      ...prev,
      [memoryId]: users
    }))
  }, [])

  /**
   * Handle content changes in collaborations
   */
  const handleContentChanged = useCallback(({ memoryId, content, metadata, changes }) => {
    console.log(`📝 Content changed for ${memoryId}`)
    
    // Update local collaborative memories
    setCollaborativeMemories(prev => 
      prev.map(memory => 
        memory.id === memoryId 
          ? { ...memory, content, metadata, lastUpdated: Date.now() }
          : memory
      )
    )
  }, [])

  /**
   * Handle sync state changes
   */
  const handleSyncStateChanged = useCallback(({ memoryId, provider, status, connected, synced }) => {
    console.log(`🔄 Sync state changed for ${memoryId}:`, { provider, status, connected, synced })
    
    if (provider === 'websocket' && connected && synced) {
      setP2PStatus('synced')
    }
  }, [])

  /**
   * Enhanced upload function that uses P2P first
   */
  const uploadMemoryWithP2P = useCallback(async (files, note, preservationMode = 'private') => {
    console.log('📤 Starting P2P-first memory upload...', { preservationMode })

    if (!files.length || !note.trim()) {
      throw new Error('Please select files and add a memory note.')
    }

    if (!isAuthenticated) {
      throw new Error('Please connect your wallet first to upload memories.')
    }

    try {
      const results = []

      for (const file of files) {
        console.log(`📁 Processing file: ${file.name}`)

        // Create memory object
        const memoryData = {
          note: note.trim(),
          title: note.trim(),
          content: note.trim(), // For text content
          type: getFileType(file),
          preservationMode: preservationMode,
          fileName: file.name,
          fileSize: file.size,
          userId: user.id,
          timestamp: Date.now(),
          tags: extractTags(note)
        }

        if (preservationMode === 'public') {
          // Use P2P collaborative system for public memories
          const result = await publishCollaborativeMemory(memoryData)
          results.push({
            ...result,
            file: file,
            memoryData: memoryData
          })
        } else {
          // Use existing upload system for private memories
          // This would call your existing uploadMemory function
          results.push({
            success: true,
            memoryData: memoryData,
            file: file,
            isCollaborative: false
          })
        }
      }

      console.log('✅ P2P-first upload completed:', results.length)
      return results
    } catch (error) {
      console.error('❌ P2P-first upload failed:', error)
      throw error
    }
  }, [isAuthenticated, user, publishCollaborativeMemory])

  /**
   * Initialize P2P when authenticated
   */
  useEffect(() => {
    if (isAuthenticated && wallet && user && !isP2PInitialized) {
      initializeP2P()
    }
  }, [isAuthenticated, wallet, user, isP2PInitialized, initializeP2P])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Disconnect from all active collaborations
      activeDocumentsRef.current.forEach((collaboration, memoryId) => {
        if (collaboration.disconnect) {
          collaboration.disconnect()
        }
      })
      activeDocumentsRef.current.clear()
      providersRef.current.clear()
      awarenessRef.current.clear()
    }
  }, [])

  return {
    // State
    isP2PInitialized,
    activeCollaborations,
    collaborativeMemories,
    p2pStatus,
    activeUsers,

    // Functions
    publishCollaborativeMemory,
    joinCollaborativeMemory,
    getArchivedMemories,
    loadActiveCollaborations,
    uploadMemoryWithP2P,

    // Collaboration interface
    getCollaboration: (memoryId) => activeDocumentsRef.current.get(memoryId),
    
    // Status
    isConnected: p2pStatus === 'connected' || p2pStatus === 'synced',
    isPublishing: p2pStatus === 'publishing'
  }
}

/**
 * Helper functions
 */
function getFileType(file) {
  const type = file.type.split('/')[0]
  const typeMap = {
    'image': 'image',
    'video': 'video', 
    'audio': 'audio',
    'text': 'document',
    'application': 'document'
  }
  return typeMap[type] || 'document'
}

function extractTags(note) {
  const tagRegex = /#(\w+)/g
  const tags = []
  let match
  while ((match = tagRegex.exec(note)) !== null) {
    tags.push(match[1].toLowerCase())
  }
  return tags
}

/**
 * Create the collaboration class (embedded version of frontend integration)
 */
function createCollaborationClass(Y, WebsocketProvider, WebrtcProvider, IndexeddbPersistence) {
  return class EtherithMemoryCollaboration {
    constructor(config = {}) {
      this.apiBase = config.apiBase || API_ENDPOINTS.YJS_P2P_PUBLISHER
      this.websocketUrl = config.websocketUrl || 'wss://y-websocket.etherith.io'
      this.userId = config.userId
      this.userName = config.userName || 'Anonymous'
      this.deviceId = config.deviceId || this.generateDeviceId()
      
      // Active documents and providers
      this.activeDocuments = new Map()
      this.providers = new Map()
      this.awareness = new Map()
      
      // UI callbacks
      this.onActiveUsersChanged = config.onActiveUsersChanged || (() => {})
      this.onContentChanged = config.onContentChanged || (() => {})
      this.onSyncStateChanged = config.onSyncStateChanged || (() => {})
    }

    async publishCollaborativeMemory(memoryData) {
      try {
        console.log('Publishing collaborative memory...')
        
        const response = await fetch(`${this.apiBase}/publish-collaborative-memory`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-ID': this.deviceId
          },
          body: JSON.stringify({
            userId: this.userId,
            title: memoryData.title,
            content: memoryData.content,
            contentType: memoryData.contentType || 'text/plain',
            tags: memoryData.tags || [],
            visibility: memoryData.visibility || 'public',
            communityId: memoryData.communityId,
            isRealtime: true // Enable real-time collaboration
          })
        })

        const result = await response.json()
        
        if (result.success) {
          // Join the collaborative session immediately
          const collaboration = await this.joinCollaborativeMemory(result.memoryId)
          return {
            success: true,
            memoryId: result.memoryId,
            collaboration,
            documentUrl: result.documentUrl
          }
        } else {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('Error publishing collaborative memory:', error)
        return { success: false, error: error.message }
      }
    }

    async joinCollaborativeMemory(memoryId) {
      try {
        console.log(`Joining collaborative memory: ${memoryId}`)

        // Check if already connected
        if (this.activeDocuments.has(memoryId)) {
          return this.getCollaborationInterface(memoryId)
        }

        // Create Yjs document
        const ydoc = new Y.Doc()
        const roomName = `memory-${memoryId}`

        // Set up providers
        const providers = {
          // WebSocket for server coordination
          ws: new WebsocketProvider(this.websocketUrl, roomName, ydoc, {
            connect: true,
            params: { 
              userId: this.userId,
              deviceId: this.deviceId 
            }
          }),

          // WebRTC for direct P2P with centralized signaling config
          webrtc: new WebrtcProvider(roomName, ydoc, {
            ...getWebRTCConfig(20),
            password: null
          }),

          // IndexedDB for offline persistence
          indexeddb: new IndexeddbPersistence(roomName, ydoc)
        }

        // Store references
        this.activeDocuments.set(memoryId, ydoc)
        this.providers.set(memoryId, providers)

        // Set up awareness
        const awareness = providers.ws.awareness
        this.awareness.set(memoryId, awareness)

        // Set local user info
        awareness.setLocalStateField('user', {
          id: this.userId,
          name: this.userName,
          color: this.generateUserColor(this.userId),
          cursor: null,
          selection: null,
          isTyping: false
        })

        // Set up event listeners
        this.setupEventListeners(memoryId, ydoc, providers, awareness)

        // Wait for initial sync
        await this.waitForSync(providers)

        // Notify server about joining
        await this.notifyServerJoin(memoryId)

        console.log(`Successfully joined collaborative memory: ${memoryId}`)
        return this.getCollaborationInterface(memoryId)

      } catch (error) {
        console.error('Error joining collaborative memory:', error)
        throw error
      }
    }

    async getArchivedMemories(filters = {}) {
      try {
        const params = new URLSearchParams()
        
        if (filters.userId) params.append('userId', filters.userId)
        if (filters.tags) params.append('tags', filters.tags.join(','))
        if (filters.visibility) params.append('visibility', filters.visibility)
        if (filters.limit) params.append('limit', filters.limit.toString())
        if (filters.offset) params.append('offset', filters.offset.toString())

        const response = await fetch(`${this.apiBase}/archived-memories?${params}`, {
          headers: {
            'X-Device-ID': this.deviceId
          }
        })

        return await response.json()
      } catch (error) {
        console.error('Error fetching archived memories:', error)
        return { memories: [], total: 0 }
      }
    }

    async getActiveCollaborativeSessions() {
      try {
        const response = await fetch(`${this.apiBase}/active-sessions`, {
          headers: {
            'X-Device-ID': this.deviceId
          }
        })

        const result = await response.json()
        return result.sessions || []
      } catch (error) {
        console.error('Error fetching active sessions:', error)
        return []
      }
    }

    setupEventListeners(memoryId, ydoc, providers, awareness) {
      const ytext = ydoc.getText('content')
      const ymeta = ydoc.getMap('metadata')

      // Content changes
      ytext.observe((event) => {
        this.onContentChanged({
          memoryId,
          content: ytext.toString(),
          changes: event.changes,
          transaction: event.transaction
        })
      })

      // Metadata changes
      ymeta.observe((event) => {
        const metadata = {}
        ymeta.forEach((value, key) => {
          metadata[key] = value
        })
        
        this.onContentChanged({
          memoryId,
          metadata,
          changes: event.changes,
          transaction: event.transaction
        })
      })

      // Awareness changes (active users)
      awareness.on('change', (changes) => {
        const activeUsers = this.getActiveUsersFromAwareness(awareness)
        this.onActiveUsersChanged({
          memoryId,
          activeUsers,
          changes
        })
      })

      // Sync state changes
      providers.ws.on('status', (event) => {
        this.onSyncStateChanged({
          memoryId,
          provider: 'websocket',
          status: event.status,
          connected: providers.ws.wsconnected,
          synced: providers.ws.synced
        })
      })

      providers.indexeddb.on('synced', () => {
        this.onSyncStateChanged({
          memoryId,
          provider: 'indexeddb',
          status: 'synced',
          synced: true
        })
      })
    }

    getCollaborationInterface(memoryId) {
      const ydoc = this.activeDocuments.get(memoryId)
      const providers = this.providers.get(memoryId)
      const awareness = this.awareness.get(memoryId)

      if (!ydoc || !providers || !awareness) {
        throw new Error(`No active collaboration found for memory: ${memoryId}`)
      }

      const ytext = ydoc.getText('content')
      const ymeta = ydoc.getMap('metadata')

      return {
        // Document access
        ydoc,
        ytext,
        ymeta,
        awareness,

        // Content methods
        getText: () => ytext.toString(),
        setText: (text) => {
          ydoc.transact(() => {
            ytext.delete(0, ytext.length)
            ytext.insert(0, text)
          })
        },
        insertText: (index, text) => ytext.insert(index, text),
        deleteText: (index, length) => ytext.delete(index, length),

        // Metadata methods
        getTitle: () => ymeta.get('title'),
        setTitle: (title) => ymeta.set('title', title),
        getTags: () => ymeta.get('tags') || [],
        setTags: (tags) => ymeta.set('tags', tags),

        // Awareness methods
        getActiveUsers: () => this.getActiveUsersFromAwareness(awareness),
        
        // Connection management
        isConnected: () => providers.ws.wsconnected,
        isSynced: () => providers.ws.synced,
        disconnect: () => this.disconnectFromMemory(memoryId)
      }
    }

    getActiveUsersFromAwareness(awareness) {
      const users = []
      const states = awareness.getStates()

      for (const [clientId, state] of states.entries()) {
        if (state.user) {
          users.push({
            clientId,
            userId: state.user.id,
            userName: state.user.name,
            color: state.user.color,
            cursor: state.user.cursor,
            selection: state.user.selection,
            isTyping: state.user.isTyping
          })
        }
      }

      return users
    }

    generateUserColor(userId) {
      const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
        '#FECA57', '#FF9FF3', '#54A0FF', '#5F27CD'
      ]
      const hash = userId.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
      return colors[hash % colors.length]
    }

    generateDeviceId() {
      return 'device-' + Math.random().toString(36).substr(2, 9)
    }

    async waitForSync(providers) {
      return new Promise((resolve) => {
        let syncedCount = 0
        const totalProviders = 2 // WS and IndexedDB

        const checkSync = () => {
          syncedCount++
          if (syncedCount >= totalProviders) {
            resolve()
          }
        }

        providers.ws.on('synced', checkSync)
        providers.indexeddb.on('synced', checkSync)

        // Fallback timeout
        setTimeout(resolve, 5000)
      })
    }

    async notifyServerJoin(memoryId) {
      try {
        await fetch(`${this.apiBase}/join-memory`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-ID': this.deviceId
          },
          body: JSON.stringify({
            memoryId,
            userId: this.userId
          })
        })
      } catch (error) {
        console.error('Error notifying server about join:', error)
      }
    }
  }
}
